/**
 * Date of birth is stored as UTC first-of-month (YYYY-MM-01) — month & year only, no day captured.
 */

const MONTH_YEAR_RE = /^(\d{4})-(\d{2})$/;
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Value for month/year controls (YYYY-MM).
 * @param {any} value
 * @returns {string}
 */
export function getBirthMonthYearInputValue(value) {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "string") {
    const s = value.trim();
    if (!s) return "";
    if (MONTH_YEAR_RE.test(s)) return s;
    if (YMD_RE.test(s)) return s.slice(0, 7);
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

/**
 * @param {any} raw
 * @returns {{ ok: boolean, monthYear: string, storageYmd: string }}
 */
export function parseBirthMonthYearStrict(raw) {
  const s = raw != null ? String(raw).trim() : "";
  if (!s) return { ok: true, monthYear: "", storageYmd: "" };

  let monthYear = "";
  if (MONTH_YEAR_RE.test(s)) {
    monthYear = s;
  } else if (YMD_RE.test(s)) {
    monthYear = s.slice(0, 7);
  } else {
    const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdy) {
      const mm = Number(mdy[1]);
      const yyyy = Number(mdy[3]);
      if (Number.isInteger(mm) && mm >= 1 && mm <= 12 && Number.isInteger(yyyy)) {
        monthYear = `${yyyy}-${String(mm).padStart(2, "0")}`;
      }
    } else {
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) {
        monthYear = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      }
    }
  }

  if (!monthYear) return { ok: false, monthYear: "", storageYmd: "" };
  const [yStr, mStr] = monthYear.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  if (!Number.isInteger(y) || y < 1900 || y > 2100) return { ok: false, monthYear: "", storageYmd: "" };
  if (!Number.isInteger(m) || m < 1 || m > 12) return { ok: false, monthYear: "", storageYmd: "" };
  return { ok: true, monthYear, storageYmd: `${monthYear}-01` };
}

/** @returns {string} Empty string when valid or optional empty; otherwise an error message. */
export function validateRequiredBirthMonthYear(raw) {
  const parsed = parseBirthMonthYearStrict(raw);
  if (!parsed.ok) return "Date of birth must be a valid month and year.";
  if (!parsed.monthYear) return "Date of birth is required.";
  return "";
}

/** @returns {string} Empty string when valid or empty; otherwise an error message. */
export function validateOptionalBirthMonthYear(raw) {
  const s = raw != null ? String(raw).trim() : "";
  if (!s) return "";
  const parsed = parseBirthMonthYearStrict(raw);
  if (!parsed.ok) return "Date of birth must be a valid month and year.";
  return "";
}

/**
 * Normalize for API persistence (YYYY-MM-01).
 * @param {any} raw
 * @returns {{ ok: boolean, error: string, storageYmd: string }}
 */
export function normalizeBirthMonthYearForSave(raw) {
  const parsed = parseBirthMonthYearStrict(raw);
  if (!parsed.ok) {
    return { ok: false, error: "Date of birth must be a valid month and year.", storageYmd: "" };
  }
  return { ok: true, error: "", storageYmd: parsed.storageYmd };
}

/**
 * Display month & year only (never the day).
 * @param {any} value
 * @returns {string}
 */
export function formatBirthMonthYearDisplay(value) {
  const monthYear = getBirthMonthYearInputValue(value);
  if (!monthYear) return "—";
  const [yStr, mStr] = monthYear.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  if (!Number.isInteger(y) || !Number.isInteger(m) || m < 1 || m > 12) return "—";
  const dt = new Date(Date.UTC(y, m - 1, 1));
  return dt.toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
}

/** @returns {{ month: string, year: string }} Two-digit month (01–12) and 4-digit year, or empty strings. */
export function splitBirthMonthYear(value) {
  const monthYear = getBirthMonthYearInputValue(value);
  if (!monthYear) return { month: "", year: "" };
  const [year, month] = monthYear.split("-");
  return { month: month || "", year: year || "" };
}

/**
 * @param {string} month Two-digit month 01–12
 * @param {string} year Up to 4 digit year string
 * @returns {string} YYYY-MM when valid and complete, otherwise ""
 */
export function combineBirthMonthYear(month, year) {
  const m = month != null ? String(month).trim() : "";
  const y = year != null ? String(year).replace(/\D/g, "").slice(0, 4) : "";
  if (!m || y.length !== 4) return "";
  const yn = Number(y);
  const mn = Number(m);
  if (!Number.isInteger(yn) || yn < 1900 || yn > 2100) return "";
  if (!Number.isInteger(mn) || mn < 1 || mn > 12) return "";
  return `${y}-${String(mn).padStart(2, "0")}`;
}

export const BIRTH_MONTH_OPTIONS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];
